"use client";

import { useCallback } from "react";
import { useNotification } from "../../../hooks/useNotification";
import { useAppDispatch } from "../../../redux/hooks";
import { addPendingImportedFilesToReport } from "../../../redux/slices/auditReportSlice.js";
import {
  filterAlreadyImportedItems,
  filterLocalFormDataBySubsections,
  getImportedFilesForSectionImport,
  getImportSubsectionOptions,
  getLocalFormDataForSectionImport,
  getObservationsForSectionImport,
  getPendingImportedFilesFromItems,
  getWorksForSectionImport,
} from "../../../utils/reportSectionImport";
import { ReportSectionImport } from "./report-section-import";

type LocalSectionImportControlProps = {
  label?: string;
  previousCompletedReports?: any[];
  sectionName: string;
  subsectionOptions?: { value: string; label: string }[];
  siteId?: string;
  reportId?: string;
  readOnly?: boolean;
  setFormData?: (value: any | ((previous: any) => any)) => void;
  workProgressData?: any[];
  setWorkProgressData?: (value: any[] | ((previous: any[]) => any[])) => void;
  observationProgressData?: any[];
  setObservationProgressData?: (
    value: any[] | ((previous: any[]) => any[]),
  ) => void;
};

export function LocalSectionImportControl({
  label = "Export Data",
  previousCompletedReports = [],
  sectionName,
  subsectionOptions = [],
  siteId,
  reportId,
  readOnly = false,
  setFormData,
  workProgressData = [],
  setWorkProgressData,
  observationProgressData = [],
  setObservationProgressData,
}: LocalSectionImportControlProps) {
  const { notify } = useNotification();
  const dispatch = useAppDispatch();
  const resolvedSubsectionOptions =
    subsectionOptions.length > 0
      ? subsectionOptions
      : getImportSubsectionOptions(sectionName);

  const handleImport = useCallback(
    (sourceReportId: string, selectedSubsections: string[] = []) => {
      const sourceReportData = previousCompletedReports.find(
        (item) => item?.report?._id === sourceReportId,
      );
      

      if (!sourceReportData) {
        notify("Selected report data is not available.", "warning");
        return;
      }

      const nextFormData = filterLocalFormDataBySubsections(
        sectionName,
        getLocalFormDataForSectionImport(sectionName, sourceReportData),
        selectedSubsections,
      );



      const nextWorks = filterAlreadyImportedItems(
        workProgressData,
        getWorksForSectionImport(
          sectionName,
          sourceReportData,
          siteId,
          reportId,
          selectedSubsections,
        ),
      );
      const importedObservations = getObservationsForSectionImport(
        sectionName,
        sourceReportData,
        siteId,
        reportId,
        selectedSubsections,
      );
      const nextObservations =
        sectionName === "key-issue-matrix"
          ? importedObservations
          : filterAlreadyImportedItems(
              observationProgressData,
              importedObservations,
            );

      const nextSectionFiles = getImportedFilesForSectionImport(
        sectionName,
        sourceReportData,
        siteId,
        reportId,
        selectedSubsections,
      );
      const nextItemFiles = [
        ...getPendingImportedFilesFromItems(nextWorks),
        ...getPendingImportedFilesFromItems(nextObservations),
      ];

      let imported = 0;

      if (setFormData && nextFormData && Object.keys(nextFormData).length > 0) {
        setFormData((previous: any) => ({
          ...(previous || {}),
          ...nextFormData,
        }));
        imported += 1;
      }

      if (setWorkProgressData && nextWorks.length > 0) {
        setWorkProgressData([...workProgressData, ...nextWorks]);
        imported += nextWorks.length;
      }

      if (setObservationProgressData && nextObservations.length > 0) {
        setObservationProgressData([
          ...observationProgressData,
          ...nextObservations,
        ]);
        imported += nextObservations.length;
      }

      if (
        sectionName !== "key-issue-matrix" &&
        (nextSectionFiles.length > 0 || nextItemFiles.length > 0)
      ) {
        dispatch(
          addPendingImportedFilesToReport({
            reportId,
            files: [...nextSectionFiles, ...nextItemFiles],
          }),
        );
        imported += nextSectionFiles.length + nextItemFiles.length;
      }

      notify(
        imported > 0
          ? "Section data imported into this form."
          : "No data was available for this section in the selected report.",
        imported > 0 ? "success" : "warning",
      );
    },
    [
      notify,
      dispatch,
      previousCompletedReports,
      reportId,
      sectionName,
      setFormData,
      observationProgressData,
      setObservationProgressData,
      workProgressData,
      setWorkProgressData,
      siteId,
    ],
  );

  return (
    <ReportSectionImport
      label={label}
      reports={previousCompletedReports}
      subsectionOptions={resolvedSubsectionOptions}
      disabled={readOnly}
      onImport={handleImport}
    />
  );
}
